<template>
  <div>
    <div class="table-container" grid-drag-ignore>
      <table class="budget-table">
        <thead class="border-bottom">
          <tr>
            <th v-for="col in sortedHeaders" :key="col.value" :class="getHeaderClass(col)">{{ col.title }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="processedData.length === 0">
            <td :colspan="headers.length" style="text-align: center">No data to display</td>
          </tr>
          <tr v-for="(row, rowIndex) in processedData" :key="rowIndex" :class="{ 'subtotal-row': row.isSubtotal }">
            <template v-for="col in sortedHeaders">
              <td
                v-if="shouldRenderCell(row, col)"
                :key="col.value"
                :rowspan="getCellRowspan(row, col)"
                :class="getCellClass(col, row)"
                @click="onCellClick(rowIndex, col, row, $event)"
              >
                <div class="cell-content">
                  <template v-if="edit && isEditable(col) && isEditing(rowIndex, col)">
                    <input
                      class="table-input-text"
                      :ref="inputRefKey(rowIndex, col)"
                      type="text"
                      v-model="editingValue"
                      inputmode="decimal"
                      @keydown.enter.prevent="commitEdit(rowIndex, col)"
                      @keydown.esc.prevent="cancelEdit"
                      @blur="cancelEdit"
                      @paste.stop.prevent="onPaste($event, col)"
                    />
                  </template>

                  <template v-else>
                    <template v-if="col.value === 'geography'">
                      <span class="geo-text">{{ getDisplayedValue(row, col) }}</span>
                      <ButtonAction
                        v-if="row?.scenario_id"
                        class="markets-btn"
                        no-tooltip
                        icon="mdi-information-outline"
                        @click="$emit('clickPlanLocation', row?.scenario_id)"
                      />
                    </template>
                    <template v-else-if="col.value === 'campaign_kpi'">
                      <span class="geo-text">{{ getDisplayedValue(row, col) }}</span>
                      <v-icon
                        v-if="getErrorText(row, col)"
                        @click="onClickTableKPIAlert"
                        size="large"
                        class="ml-1 mb-1 output-alert-icon"
                        >mdi-alert-circle</v-icon
                      >
                    </template>
                    <template v-else>
                      {{ getDisplayedValue(row, col) }}
                    </template>
                  </template>
                </div>
              </td>
            </template>
          </tr>
        </tbody>
        <tfoot>
          <tr class="subtotal-row">
            <td :colspan="1" class="group-cell">Grand Total</td>
            <template v-for="col in sortedHeaders.slice(1)">
              <td :key="col.value" :class="getFooterCellClass(col)">
                {{ isNumeric(col) ? getGrandTotalValue(col) : "" }}
              </td>
            </template>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { cloneDeep } from "lodash";
import Vue, { PropType } from "vue";
import { mapGetters } from "vuex";

import ButtonAction from "@/components/customComponents/ButtonAction.vue";
import { weightedMap } from "@/constants";
import csvExport, { buildFileName, buildHeaderRows } from "@/helpers/csvExport";
import { formatters } from "@/helpers/formatters";
import Lang from "@/lang";
import { ProjectService } from "@/services/projectService";
import planTableMetrics from "@/settings/metrics/planTableMetrics";
import { PlanInputDictionary } from "@/types/common";
import { NUMERIC_KEYS, NumericKey } from "@/types/project";
import { BusinessGoal, MetricsRowInput, ProcessedRow, RecalculatePlanInput, RowIdentity } from "@/types/tables";

const masterOrderMap = new Map<string, number>();
planTableMetrics.forEach((metric, index) => {
  masterOrderMap.set(metric.value, index);
});

const UI_TO_API: Record<string, NumericKey> = {
  budget: "channel_budget",
};
const VALIDATION = {
  pctTolerance: 0.01,
  budgetTolerance: 0.01,
};
const toInt = (n: number): number => {
  const v = Number(n);
  return Number.isFinite(v) ? Math.trunc(v) : 0;
};

function applyFormatter(col: PlanInputDictionary, val: unknown): unknown {
  if (typeof col.formatter !== "function") return val;

  let fval: string | number | null;
  if (val === undefined || val === null) fval = null;
  else if (typeof val === "number" || typeof val === "string") fval = val;
  else {
    const asNum = Number(val);
    fval = Number.isFinite(asNum) ? asNum : String(val);
  }

  try {
    return col.formatter(fval);
  } catch (e) {
    console.error("[export] formatter error for", col.value, e);
    return val;
  }
}

export default Vue.extend({
  name: "ProjectOutputTable",
  components: { ButtonAction },
  props: {
    data: {
      type: Array as PropType<BusinessGoal[]>,
      required: true,
    },
    headers: {
      type: Array as PropType<PlanInputDictionary[]>,
      required: true,
    },
    edit: Boolean,
  },
  data() {
    return {
      editingCell: { rowIndex: -1, colKey: "" as string },
      editingValue: "" as string,
      updateDraft: {
        metrics_rows: [] as MetricsRowInput[],
        index: {} as Record<string, number>,
        scenario_dates_by_id: {} as Record<string, { scenario_id: string; start_date: string; end_date: string }>,
      },
      validationErrors: new Set<string>(),
    };
  },
  computed: {
    ...mapGetters("projectOutput", ["projectFiltered"]),

    canReset(): boolean {
      return this.hasPendingEdits || this.validationErrors.size > 0 || this.editingCell.rowIndex !== -1;
    },
    hasPendingEdits(): boolean {
      return this.updateDraft.metrics_rows.length > 0;
    },
    isValid(): boolean {
      return this.validationErrors.size === 0;
    },
    canRecalculate(): boolean {
      return this.hasPendingEdits && this.isValid;
    },

    sortedHeaders(): PlanInputDictionary[] {
      const headersCopy = [...this.headers];
      headersCopy.sort((a, b) => {
        const levelDifference = a.lvl - b.lvl;
        if (levelDifference !== 0) {
          return levelDifference;
        }

        const aOriginalIndex = masterOrderMap.get(a.value) ?? Infinity;
        const bOriginalIndex = masterOrderMap.get(b.value) ?? Infinity;

        return aOriginalIndex - bOriginalIndex;
      });
      return headersCopy;
    },
    processedData(): ProcessedRow[] {
      const processedRows: ProcessedRow[] = [];
      if (!this.data || !Array.isArray(this.data)) return [];

      this.data.forEach((bg) => {
        let firstOfBg = true;
        const bgGroupRows: ProcessedRow[] = [];

        bg.children?.forEach((plan) => {
          let firstOfPlan = true;
          const planGroupRows: ProcessedRow[] = [];

          plan.children?.forEach((scenario) => {
            const scenarioGroupRows: ProcessedRow[] = [];

            const groupsByKpi = new Map<string, ProcessedRow[]>();

            scenario.rows.forEach((row) => {
              const kpi = row["campaign_kpi"] || "Other";

              const fullRow: ProcessedRow = {
                ...row,
                business_goal: bg.business_goal,
                campaign_id: bg.campaign_id,
                plan: plan.plan,
                geography: scenario.geography,
                campaign_kpi: kpi,
                scenario_id: scenario.scenario_id,
                start_date: scenario.start_date,
                end_date: scenario.end_date,
              };

              if (!groupsByKpi.has(kpi)) {
                groupsByKpi.set(kpi, []);
              }
              groupsByKpi.get(kpi)!.push(fullRow);
            });

            const sortedKpis = Array.from(groupsByKpi.keys()).sort();
            let firstKpiInScenario = true;

            for (const kpi of sortedKpis) {
              const groupRows = groupsByKpi.get(kpi)!;

              groupRows.forEach((row, i) => {
                row.showBusinessGoal = firstOfBg;
                row.showPlan = firstOfPlan;
                row.showScenario = firstKpiInScenario && i === 0;

                firstOfBg = false;
                firstOfPlan = false;
              });

              scenarioGroupRows.push(...groupRows);

              const subtotal: ProcessedRow = { isSubtotal: true };

              this.sortedHeaders.forEach((h) => {
                if (h.lvl !== 2) return;

                if (this.isNumeric(h)) {
                  const values = groupRows.map((r) => Number(r[h.value]) || 0);
                  const localWeightedMap = cloneDeep(weightedMap);
                  const metricInfo = localWeightedMap[h.value];

                  if (metricInfo) {
                    const numSum = groupRows.reduce((sum, r) => sum + (Number(r[metricInfo.num]) || 0), 0);
                    const denomSum = groupRows.reduce((sum, r) => sum + (Number(r[metricInfo.denom]) || 0), 0);
                    subtotal[h.value] =
                      numSum && denomSum
                        ? metricInfo.multiplier
                          ? (metricInfo.multiplier * numSum) / denomSum
                          : numSum / denomSum
                        : 0;
                  } else {
                    subtotal[h.value] = values.reduce((sum, v) => sum + v, 0);
                  }
                } else if (h.value === "campaign_kpi") {
                  subtotal[h.value] = "Subtotal:";
                } else {
                  subtotal[h.value] = "";
                }
              });

              scenarioGroupRows.push(subtotal);
              firstKpiInScenario = false;
            }

            if (scenarioGroupRows.length > 0) {
              scenarioGroupRows[0].scenarioRowspan = scenarioGroupRows.length;
              planGroupRows.push(...scenarioGroupRows);
            }
          });

          if (planGroupRows.length > 0) {
            planGroupRows[0].planRowspan = planGroupRows.length;
            bgGroupRows.push(...planGroupRows);
          }
        });

        if (bgGroupRows.length > 0) {
          bgGroupRows[0].bgRowspan = bgGroupRows.length;
          processedRows.push(...bgGroupRows);
        }
      });

      return processedRows;
    },
  },
  methods: {
    onClickTableKPIAlert() {
      this.$store.dispatch("confirmDialog/open", {
        title: Lang.budgetAllocationErrorTitle,
        message: Lang.budgetAllocationError,
        cancelText: Lang.ok,
        confirmText: "",
      });
    },
    sanitizePastedNumeric(raw: string, dropFraction = false): string {
      let s = raw.trim().replace(/[^\d.,-]+/g, "");

      const lastComma = s.lastIndexOf(",");
      const lastDot = s.lastIndexOf(".");
      const decIdx = Math.max(lastComma, lastDot);

      let intPart = s;
      let decPart = "";

      if (decIdx > -1) {
        intPart = s.slice(0, decIdx);
        decPart = s.slice(decIdx + 1);
      }

      intPart = intPart.replace(/[.,]/g, "");

      if (dropFraction) {
        return intPart.replace(/^0+(?=\d)/, "") || "0";
      }

      decPart = decPart.replace(/[^\d]/g, "");
      const numStr = decPart ? `${intPart}.${decPart}` : intPart;
      return numStr.replace(/^0+(?=\d)/, "") || "0";
    },

    onPaste(e: ClipboardEvent, col: PlanInputDictionary) {
      const text = (e.clipboardData?.getData("text") ?? "").trim();

      if (!this.isNumeric(col)) {
        this.editingValue = text;
        return;
      }
      const isBudget = col.value === "budget" || UI_TO_API[col.value] === "channel_budget";
      this.editingValue = this.sanitizePastedNumeric(text, isBudget);
    },
    buildExportMatrix(): unknown[][] {
      const cols = this.sortedHeaders;
      const keys = this.getExportKeys();

      const out: unknown[][] = [this.getExportHeaderRow()];

      for (const row of this.processedData) {
        const csvRow: unknown[] = [];

        this.sortedHeaders.forEach((col, i) => {
          const uiKey = String(col.value);
          const dataKey = keys[i];

          if (row.isSubtotal) {
            if (col.lvl !== 2) {
              csvRow.push("");
              return;
            }
            const raw = row[dataKey] ?? row[uiKey] ?? "";
            const outVal = this.isNumeric(col) ? applyFormatter(col, raw) : raw;
            csvRow.push(outVal);
            return;
          }

          if (col.lvl === 0 && !row.showBusinessGoal) {
            csvRow.push("");
            return;
          }
          if (col.lvl === 1) {
            if (uiKey === "plan" && !row.showPlan) {
              csvRow.push("");
              return;
            }
            if (uiKey === "geography" && !row.showScenario) {
              csvRow.push("");
              return;
            }
          }

          let val: unknown;
          if (this.isNumeric(col) && col.lvl === 2) {
            val = this.getPatchedNumeric(row, dataKey as NumericKey);
          } else {
            val = row[dataKey] ?? row[uiKey] ?? "";
          }

          if (this.isNumeric(col)) {
            val = applyFormatter(col, val);
          }

          csvRow.push(val);
        });

        out.push(csvRow);
      }

      if (this.processedData.length) {
        const totalRow: unknown[] = [];
        cols.forEach((col, idx) => {
          if (idx === 0) {
            totalRow.push("Grand Total");
            return;
          }
          totalRow.push(this.isNumeric(col) ? this.getGrandTotalValue(col) : "");
        });
        out.push(totalRow);
      }

      return out;
    },

    exportProcessedData(): void {
      const matrix = this.buildExportMatrix();

      const title = "Projected Recommendations";

      const csvContent: unknown[][] = [];
      csvContent.push(...buildHeaderRows(title));
      csvContent.push([]);
      csvContent.push(...matrix);

      const fileName = buildFileName(title);
      csvExport(csvContent, fileName);
    },
    getExportKeys(): string[] {
      return this.sortedHeaders.map((h) => UI_TO_API[String(h.value)] ?? String(h.value));
    },

    getExportHeaderRow(): string[] {
      return this.sortedHeaders.map((h) => String(h.title));
    },
    getRawEditableValue(row: ProcessedRow, col: PlanInputDictionary): unknown {
      const id = this.makeIdentity(row);
      const key = this.identityKey(id);
      const idx = this.updateDraft.index[key];
      const draftRow = idx !== undefined ? this.updateDraft.metrics_rows[idx] : undefined;

      const dataKey = (UI_TO_API[col.value] ?? col.value) as string;

      if (draftRow && Object.prototype.hasOwnProperty.call(draftRow, dataKey)) {
        return (draftRow as Record<string, unknown>)[dataKey];
      }
      return row[dataKey];
    },
    resetDraft() {
      this.cancelEdit();
      this.validationErrors = new Set<string>();
      this.$set(this, "updateDraft", {
        metrics_rows: [],
        index: {},
        scenario_dates_by_id: {},
      });
    },
    getGroupOriginalBudgetTotal(base: ProcessedRow): number {
      const peers = this.getGroupRows(base);
      return peers.reduce((acc, r) => acc + toInt(Number((r as Record<string, unknown>).channel_budget) || 0), 0);
    },

    validateGroupTotals(changedRow: ProcessedRow) {
      const peers = this.getGroupRows(changedRow);
      const newErrors = new Set<string>();

      if (this.hasAnyPatched(peers, "channel_percentage")) {
        const sumPct = this.sumGroup(peers, "channel_percentage", true);
        const okPct = Math.abs(sumPct - 100) <= VALIDATION.pctTolerance;

        if (!okPct) {
          peers.forEach((r) => {
            newErrors.add(this.cellValidationKey(r, "channel_percentage"));
          });
        }
      }

      if (this.hasAnyPatched(peers, "channel_budget")) {
        const orig = this.sumGroup(peers, "channel_budget", false);
        const patched = this.sumGroup(peers, "channel_budget", true);
        const okBudget = Math.abs(patched - orig) <= VALIDATION.budgetTolerance;

        if (!okBudget) {
          peers.forEach((r) => {
            newErrors.add(this.cellValidationKey(r, "channel_budget"));
          });
        }
      }

      this.validationErrors = newErrors;
    },

    getDraftIndexByRow(row: ProcessedRow): number | undefined {
      const id = this.makeIdentity(row);
      const key = this.identityKey(id);
      return this.updateDraft.index[key];
    },

    getDraftRowByRow(row: ProcessedRow): MetricsRowInput | undefined {
      const idx = this.getDraftIndexByRow(row);
      return idx !== undefined ? this.updateDraft.metrics_rows[idx] : undefined;
    },

    getPatchedNumeric(row: ProcessedRow, field: NumericKey): number {
      const draft = this.getDraftRowByRow(row);
      const patched =
        draft && Object.prototype.hasOwnProperty.call(draft, field)
          ? Number((draft as Record<string, unknown>)[field])
          : undefined;
      const original = Number((row as Record<string, unknown>)[field]);
      const v = patched !== undefined ? patched : original;

      if (field === "channel_budget") {
        return toInt(v);
      }
      return Number.isFinite(v) ? Number(v) : 0;
    },

    getGroupRows(base: ProcessedRow): ProcessedRow[] {
      return this.processedData.filter(
        (r) =>
          !r.isSubtotal &&
          String(r.scenario_id) === String(base.scenario_id) &&
          String(r.campaign_kpi) === String(base.campaign_kpi)
      );
    },

    cellValidationKey(row: ProcessedRow, field: string) {
      const id = this.identityKey(this.makeIdentity(row));
      return `${id}::${field}`;
    },

    hasAnyPatched(peers: ProcessedRow[], field: NumericKey): boolean {
      return peers.some((r) => {
        const draft = this.getDraftRowByRow(r);
        return !!draft && Object.prototype.hasOwnProperty.call(draft, field);
      });
    },

    sumGroup(peers: ProcessedRow[], field: NumericKey, usePatched: boolean): number {
      return peers.reduce((acc, r) => {
        let v = usePatched ? this.getPatchedNumeric(r, field) : Number(r[field]) || 0;
        if (field === "channel_budget") v = toInt(v);
        return acc + (Number.isFinite(v) ? v : 0);
      }, 0);
    },
    isEditedCell(row: ProcessedRow, col: PlanInputDictionary): boolean {
      if (row.isSubtotal) return false;
      if (!this.isNumeric(col) || col.lvl !== 2) return false;

      const id = this.makeIdentity(row);
      const key = this.identityKey(id);
      const idx = this.updateDraft.index[key];
      if (idx === undefined) return false;

      const draftRow = this.updateDraft.metrics_rows[idx];
      const field = (UI_TO_API[col.value] ?? col.value) as string;

      if (!NUMERIC_KEYS.has(field as NumericKey)) return false;

      const hasPatched = Object.prototype.hasOwnProperty.call(draftRow, field);
      if (!hasPatched) return false;

      const original = Number(row[field]) || 0;
      const patched = Number((draftRow as Record<string, unknown>)[field]);
      if (Number.isNaN(patched)) return false;

      return patched !== original;
    },
    getCellClass(col: PlanInputDictionary, row?: ProcessedRow): Record<string, boolean> {
      if (!row) return {};

      const isEdited = this.isEditedCell(row, col);
      const key = this.cellValidationKey(row, UI_TO_API[col.value] ?? col.value);
      const hasError = this.validationErrors.has(key);

      return {
        "group-cell": col.lvl < 2,
        "text-right": this.isNumeric(col),
        "border-right": col.lvl < 2,
        "border-bottom": col.lvl <= 2,
        "edited-cell": isEdited && !hasError,
        "error-cell": hasError,
      };
    },

    findSourceRow(id: RowIdentity): ProcessedRow | undefined {
      return this.processedData.find(
        (pr: any) =>
          !pr.isSubtotal &&
          String(pr.business_goal) === id.business_goal &&
          String(pr.campaign_id) === id.campaign_id &&
          String(pr.campaign_kpi) === id.campaign_kpi &&
          String(pr.plan) === id.plan &&
          String(pr.channel) === id.channel &&
          String(pr.media_type ?? "") === id.media_type &&
          String(pr.scenario_id ?? "") === id.scenario_id
      );
    },

    valueOrZero(v: unknown): number {
      return typeof v === "number" && isFinite(v) ? v : 0;
    },

    hydrateRowFromSourceAndPatch(patch: MetricsRowInput): MetricsRowInput {
      const id: RowIdentity = {
        business_goal: patch.business_goal,
        campaign_id: patch.campaign_id,
        campaign_kpi: patch.campaign_kpi,
        plan: patch.plan,
        channel: patch.channel,
        media_type: patch.media_type,
        scenario_id: patch.scenario_id,
      };

      const src = this.findSourceRow(id);

      const base: MetricsRowInput = { ...id };
      NUMERIC_KEYS.forEach((k) => {
        base[k] = this.valueOrZero(src ? (src as Record<string, unknown>)[k] : 0);
      });

      Object.keys(patch).forEach((k) => {
        if (NUMERIC_KEYS.has(k as NumericKey)) {
          (base as Record<string, unknown>)[k] = (patch as Record<string, unknown>)[k];
        }
      });

      return base;
    },
    makeIdentity(row: ProcessedRow): RowIdentity {
      return {
        business_goal: String(row.business_goal ?? ""),
        campaign_id: String(row.campaign_id ?? ""),
        campaign_kpi: String(row.campaign_kpi ?? ""),
        plan: String(row.plan ?? ""),
        channel: String(row.channel ?? ""),
        media_type: String(row.media_type ?? ""),
        scenario_id: String(row.scenario_id ?? ""),
      };
    },
    identityKey(id: RowIdentity): string {
      const entries: [keyof RowIdentity, string][] = [
        ["business_goal", id.business_goal],
        ["campaign_id", id.campaign_id],
        ["campaign_kpi", id.campaign_kpi],
        ["plan", id.plan],
        ["channel", id.channel],
        ["media_type", id.media_type],
        ["scenario_id", id.scenario_id],
      ];
      return entries.map(([k, v]) => `${k}=${encodeURIComponent(v ?? "")}`).join("&");
    },

    applyEdit(row: ProcessedRow, field: string, valueRaw: string) {
      const target = (UI_TO_API[field] ?? field) as string;
      if (!NUMERIC_KEYS.has(target as NumericKey)) return;

      let value = Number(valueRaw);
      if (!Number.isFinite(value)) return;

      const id = this.makeIdentity(row);
      const key = this.identityKey(id);

      let idx = this.updateDraft.index[key];
      if (idx === undefined) {
        idx = this.updateDraft.metrics_rows.length;
        this.updateDraft.index[key] = idx;
        this.updateDraft.metrics_rows.push({ ...id });

        const sid = id.scenario_id;
        if (sid && !this.updateDraft.scenario_dates_by_id[sid]) {
          this.$set(this.updateDraft.scenario_dates_by_id, sid, {
            scenario_id: sid,
            start_date: String(row.start_date),
            end_date: String(row.end_date),
          });
        }
      }

      const round2 = (n: number) => Math.round(n * 100) / 100;

      const TOTAL = toInt(this.getGroupOriginalBudgetTotal(row));
      const draft = { ...this.updateDraft.metrics_rows[idx] };

      if (target === "channel_percentage") {
        value = Math.max(0, Math.min(100, value));
        draft.channel_percentage = round2(value);

        draft.channel_budget = TOTAL > 0 ? toInt((value / 100) * TOTAL) : 0;
      } else if (target === "channel_budget") {
        value = Math.max(0, value);
        const budgetInt = toInt(value);
        draft.channel_budget = budgetInt;

        const newPct = TOTAL > 0 ? (budgetInt / TOTAL) * 100 : 0;
        draft.channel_percentage = round2(newPct);
      } else {
        (draft as Record<string, unknown>)[target] = value;
      }

      this.$set(this.updateDraft.metrics_rows, idx, draft);
    },
    websterRoundSingle(value: number, allValues: number[]): number {
      if (!Array.isArray(allValues) || allValues.length === 0) return 0;

      const targetTotal = Math.round(allValues.reduce((a, b) => a + (Number(b) || 0), 0));

      if (targetTotal <= 0) return 0;

      const entries: { score: number; index: number }[] = [];

      allValues.forEach((vRaw, i) => {
        const v = Number(vRaw);
        if (!isFinite(v) || v < 0) return;

        for (let k = 0; k < targetTotal; k++) {
          const divisor = 2 * k + 1;
          entries.push({ score: v / divisor, index: i });
        }
      });

      entries.sort((a, b) => b.score - a.score);

      const result = new Array(allValues.length).fill(0);
      let used = 0;

      for (let i = 0; i < entries.length && used < targetTotal; i++) {
        const idx = entries[i].index;
        result[idx]++;
        used++;
      }

      let targetIndex = -1;
      for (let i = 0; i < allValues.length; i++) {
        if (allValues[i] === value) {
          targetIndex = i;
          break;
        }
      }

      if (targetIndex === -1) return 0;

      return result[targetIndex];
    },

    getErrorText(row: ProcessedRow, col: PlanInputDictionary) {
      if (col.value !== "campaign_kpi" || row?.isSubtotal) return;
      return this.pickStatusSummary(row);
    },

    pickStatusSummary(row: any) {
      const projectService = new ProjectService(this.projectFiltered);
      const sc = projectService.getScenarioById(row.scenario_id);

      return this.checkMissingSample(sc?.output?.status_summary, row.campaign_kpi);
    },
    checkMissingSample(statusSummary: string[] | null | undefined, targetKpi: string): string | null {
      if (!statusSummary) return null;

      const kpiRegex = /\[Campaign KPI: (.*?)\]/;
      const missingRegex = /No similar campaign found for MMM after subset search/;

      const found = statusSummary.some((line) => {
        const match = line.match(kpiRegex);
        const kpi = match?.[1]?.trim();

        if (kpi !== targetKpi) return false;

        return missingRegex.test(line);
      });

      if (found) {
        return "Sample for KPI/Channel combination not found. Assign budget to channel in Input tab and generate to get results.";
      }

      return null;
    },
    getDisplayedValue(row: ProcessedRow, col: PlanInputDictionary) {
      const id = this.makeIdentity(row);
      const key = this.identityKey(id);
      const idx = this.updateDraft.index[key];
      const draftRow = idx !== undefined ? this.updateDraft.metrics_rows[idx] : undefined;

      const displayKey = (UI_TO_API[col.value] ?? col.value) as string;
      const patched =
        draftRow && displayKey in draftRow ? (draftRow as Record<string, unknown>)[displayKey] : undefined;

      const raw = patched !== undefined ? patched : row[col.value];

      if (!row.isSubtotal && displayKey === "channel_percentage" && !this.edit) {
        const groupRows = this.processedData.filter(
          (r) => !r.isSubtotal && r.scenario_id === row.scenario_id && r.campaign_kpi === row.campaign_kpi
        );

        const percentages = groupRows.map((r) => Number(r.channel_percentage ?? 0));

        const roundedValue = this.websterRoundSingle(row[col.value], percentages);
        return formatters.compactPercentFormat(roundedValue);
      } else if (row.isSubtotal && displayKey === "channel_percentage" && !this.edit) {
        return formatters.integerPercentDirectFormat(raw);
      }

      if (this.edit && displayKey === "channel_percentage") {
        return typeof raw === "number" ? raw.toFixed(2).replace(/\.?0+$/, "") : raw ?? "";
      }

      if (col.formatter) {
        try {
          return this.isNumeric(col) ? col.formatter(raw, { notation: "compact" }) : col.formatter(raw);
        } catch (e) {
          console.error(e);
        }
      }

      return raw;
    },

    buildFinalPayload(): RecalculatePlanInput {
      const scenario_dates = Object.values(this.updateDraft.scenario_dates_by_id);

      const metrics_rows = this.updateDraft.metrics_rows.map((patch) => this.hydrateRowFromSourceAndPatch(patch));

      return { scenario_dates, metrics_rows };
    },

    async onClickPreparePayload() {
      const payload = this.buildFinalPayload();
      const errors = this.validateUpdatePayload(payload);

      if (errors.length) {
        console.error("[/update_plan] payload validation failed:", errors);
        return;
      }

      try {
        await this.$store.dispatch("projectOutput/recalculatePlan", payload);

        this.resetDraft();
      } catch (e) {
        console.error(e);
      }
    },
    onClickResetEdits() {
      this.resetDraft();
    },

    validateUpdatePayload(payload: RecalculatePlanInput): string[] {
      const errs: string[] = [];

      if (!payload.metrics_rows || payload.metrics_rows.length === 0) {
        errs.push("metrics_rows is empty.");
      }

      for (const [i, row] of payload.metrics_rows.entries()) {
        if (!row.scenario_id) {
          errs.push(`metrics_rows[${i}]: missing scenario_id`);
        }
        const requiredIds: (keyof MetricsRowInput)[] = [
          "business_goal",
          "campaign_id",
          "campaign_kpi",
          "plan",
          "channel",
          // "media_type",
        ];
        for (const k of requiredIds) {
          if (!String(row[k] ?? "").length) {
            errs.push(`metrics_rows[${i}]: field ${k} is empty`);
          }
        }
      }

      const haveDates = new Map(payload.scenario_dates.map((d) => [d.scenario_id, d]));
      const neededIds = new Set(payload.metrics_rows.map((r) => r.scenario_id));
      for (const sid of neededIds) {
        if (!sid) continue;
        if (!haveDates.has(sid)) {
          errs.push(`scenario_dates: no record for scenario_id=${sid}`);
        }
      }

      return errs;
    },

    isEditable(col: PlanInputDictionary): boolean {
      return !!col.editable;
    },
    inputRefKey(rowIndex: number, col: PlanInputDictionary) {
      return `edit:${rowIndex}:${col.value}`;
    },
    cellKey(rowIndex: number, col: PlanInputDictionary) {
      return `${rowIndex}:${col.value}`;
    },
    isEditing(rowIndex: number, col: PlanInputDictionary) {
      return this.editingCell.rowIndex === rowIndex && this.editingCell.colKey === col.value;
    },
    onCellClick(rowIndex: number, col: PlanInputDictionary, row: ProcessedRow, e: MouseEvent) {
      if (row.isSubtotal) return;
      if (!(this.edit && this.isEditable(col))) return;

      e.stopPropagation();

      this.editingCell = { rowIndex, colKey: col.value as string };

      const raw = this.getRawEditableValue(row, col);

      if (this.isNumeric(col)) {
        const num = Number(raw);
        if (col.value === "budget" || UI_TO_API[col.value] === "channel_budget") {
          this.editingValue = Number.isFinite(num) ? String(toInt(num)) : "";
        } else {
          this.editingValue = Number.isFinite(num) ? num.toFixed(2) : "";
        }
      } else {
        this.editingValue = String(raw ?? "");
      }

      this.$nextTick(() => {
        const ref = this.$refs[this.inputRefKey(rowIndex, col)] as HTMLInputElement | HTMLInputElement[] | undefined;
        const el = Array.isArray(ref) ? ref[0] : ref;
        el?.focus();
        el?.select();
      });
    },
    commitEdit(rowIndex: number, col: PlanInputDictionary) {
      const row = this.processedData[rowIndex];
      if (!row || row.isSubtotal) {
        this.cancelEdit();
        return;
      }

      this.applyEdit(row, String(col.value), this.editingValue);

      this.validateGroupTotals(row);

      this.editingCell = { rowIndex: -1, colKey: "" };
      this.editingValue = "";
    },

    cancelEdit() {
      this.editingCell = { rowIndex: -1, colKey: "" };
      this.editingValue = "";
    },
    getGrandTotalValue(col: PlanInputDictionary): string | number {
      const dataRows = this.processedData.filter((r) => !r.isSubtotal);

      if (col.value === "campaign_kpi") {
        return "Grand Total:";
      }

      if (col.value === "channel_percentage") {
        return "";
      }

      if (!this.isNumeric(col)) {
        return "";
      }

      const localWeightedMap = cloneDeep(weightedMap);
      const metricInfo = localWeightedMap[col.value];

      let result: number;

      if (metricInfo) {
        const numSum = dataRows.reduce((sum, r) => sum + (Number(r[metricInfo.num]) || 0), 0);
        const denomSum = dataRows.reduce((sum, r) => sum + (Number(r[metricInfo.denom]) || 0), 0);

        result =
          numSum && denomSum
            ? metricInfo.multiplier
              ? (metricInfo.multiplier * numSum) / denomSum
              : numSum / denomSum
            : 0;
      } else {
        const values = dataRows.map((r) => Number(r[col.value]) || 0);
        result = values.reduce((sum, v) => sum + v, 0);
      }

      return col.formatter ? col.formatter(result, { notation: "compact" }) : result;
    },
    shouldRenderCell(row: ProcessedRow, col: PlanInputDictionary): boolean {
      if (row.isSubtotal) {
        return col.lvl === 2;
      }
      if (col.lvl === 0) return !!row.showBusinessGoal;
      if (col.lvl === 1) {
        if (col.value === "plan") return !!row.showPlan;
        if (col.value === "geography") return !!row.showScenario;
      }
      return col.lvl === 2;
    },
    getCellRowspan(row: ProcessedRow, col: PlanInputDictionary): number {
      if (row.isSubtotal) return 1;
      if (col.lvl === 0) return row.bgRowspan || 1;
      if (col.value === "plan") return row.planRowspan || 1;
      if (col.value === "geography") return row.scenarioRowspan || 1;
      return 1;
    },

    getFooterCellClass(col: PlanInputDictionary): Record<string, boolean> {
      return {
        "group-cell": col.lvl < 2,
        "text-right": this.isNumeric(col),
        "border-bottom": col.lvl <= 2,
      };
    },
    getHeaderClass(col: PlanInputDictionary): Record<string, boolean> {
      return {
        "text-right": this.isNumeric(col),
      };
    },
    getCellValue(row: ProcessedRow, col: PlanInputDictionary): string | number {
      const value = row[col.value];
      if (col.formatter) {
        return col.formatter(value, { notation: "compact" });
      }
      return value;
    },
    isNumeric(col: PlanInputDictionary): boolean {
      return col.tableGroup === "standard" || col.tableGroup === "calculated";
    },
  },
  watch: {
    canRecalculate: {
      immediate: true,
      handler(val: boolean) {
        this.$emit("canRecalculateChange", val);
      },
    },
    canReset: {
      immediate: true,
      handler(val: boolean) {
        this.$emit("canResetChange", val);
      },
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/custom_variables";

.table-container {
  overflow-x: auto;
}
.budget-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  border-bottom: 1px solid #ffffff14;
}
.budget-table th,
.budget-table td {
  padding: 8px;
  text-align: left;
  white-space: nowrap;
  vertical-align: middle;
}
.budget-table th {
  font-weight: 600;
  top: 0;
}
.text-right {
  text-align: right;
}
.group-cell {
  font-weight: 500;
  vertical-align: top;
}
.border-right {
  border-right: 1px solid #ffffff14;
}

.border-bottom {
  border-bottom: 1px solid #ffffff14;
}

.subtotal-row {
  font-weight: bold;
  background: var(--v-background-medium-base) !important;

  &:hover {
    background: var(--v-background-medium-base) !important;
  }
}
.cell-content {
  position: relative;
  width: 100%;
}

.table-input-text {
  background: #151728;
  z-index: 9999;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 75px !important;
  max-width: 75px !important;
  box-sizing: border-box;
  color: white !important;
}

.error-cell {
  background: transparent !important;
  outline: 2px solid #ff4d4f;
  outline-offset: -4px;
  border-radius: 8px;
}

.v-application.theme--dark {
  .data-table {
    tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper):not(.subtotal-row) {
      background-color: unset !important;
    }
  }
}
.markets-btn {
  ::v-deep .v-icon {
    color: #62e9a952 !important;
  }
}
::v-deep .v-btn--icon.v-size--default {
  height: initial !important;
  width: initial !important;
  margin-bottom: 2px;
}

.output-alert-icon {
  opacity: 0.7;
  color: var(--v-error-base) !important;
}
</style>
